import { Clock, Download, FileText, Replace, Trash2 } from 'lucide-react';

import { buildDownloadUrl, getDocumentMeta } from '../../pages/documents/documents-format';
import { type DocumentSummary } from '../../services/api/documents';
import truncated from '../../utils/wordsLength';
import { Button } from '../ui';

interface DocumentRowProps {
  document: DocumentSummary;
  animationDelayMs: number;
  isReplacing: boolean;
  isDeleting: boolean;
  onReplace: (documentId: string) => void;
  onDelete: (document: DocumentSummary) => void;
}

export const DocumentRow = ({
  document,
  animationDelayMs,
  isReplacing,
  isDeleting,
  onReplace,
  onDelete,
}: DocumentRowProps) => {
  const meta = getDocumentMeta(document);

  return (
    <div
      className="group animate-fade-in rounded-xl border border-brand-blue/10 bg-gradient-to-br from-white/5 to-white/10 p-6 backdrop-blur-sm transition-all duration-500 hover:border-brand-blue/30 hover:shadow-lg hover:shadow-brand-blue/10"
      style={{ animationDelay: `${animationDelayMs}ms` }}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex-1">
          <div className="mb-3 flex items-start gap-3">
            <div className="rounded-lg bg-brand-blue/10 p-2 text-brand-blue transition-transform duration-300 group-hover:scale-110">
              <FileText size={20} />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-brand-blue transition-colors group-hover:text-brand-blue/90">
                {document.title}
              </h4>
              {document.description && (
                <p className="mt-1 text-sm leading-relaxed text-brand-blue/60">
                  {truncated(document.description, 20)}
                </p>
              )}
            </div>
          </div>
          {meta && (
            <div className="flex items-center gap-2 text-xs text-brand-blue/40">
              <Clock size={14} />
              {meta}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="rounded-full border border-brand-blue/20 bg-brand-blue/10 px-3 py-1 text-xs font-medium text-brand-blue">
            {document.mimeType ?? 'Okänt format'}
          </span>
          <a
            href={buildDownloadUrl(document.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-lg border border-brand-blue/20 bg-brand-blue/10 px-3 py-2 text-sm font-medium text-brand-blue transition-all duration-300 hover:border-brand-blue/40 hover:bg-brand-blue/20 group-hover:scale-105"
          >
            <Download size={16} className="mr-2" />
            Visa
          </a>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isReplacing}
            onClick={() => onReplace(document.id)}
          >
            {isReplacing ? (
              <>
                <span className="mr-1 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Ersätter…
              </>
            ) : (
              <>
                <Replace size={14} className="mr-1" />
                Ersätt innehåll
              </>
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isDeleting}
            onClick={() => onDelete(document)}
          >
            <Trash2 size={14} className="mr-1" />
            Ta bort
          </Button>
        </div>
      </div>
    </div>
  );
};
